import { Service, ServiceSellPrice, DEFAULT_CURRENCY } from "@turizi/shared";
import { useTranslation } from 'react-i18next';
import { Button, Switch, Tooltip } from '@mantine/core';
import { IconPlus, IconEdit, IconCalendar } from "@tabler/icons-react";
import { MoneyNumberInput } from '../snippets';
import { useEffect, useState } from 'react';
import { useCreateServiceSellPrice } from "@/hooks/api-hooks";
import { notify } from "@/lib/notify";
import { formatDay } from "@/lib/format-utils";
import CurrencySelector from "../currency-selector";
import { DatePickerInput } from "@mantine/dates";
import dayjs from "dayjs";
import CardModal from "../card-modal";

interface Props {
    service: Service;
    price: ServiceSellPrice;
    label?: string;
}

export default function EditSinglePrice({
    service,
    price,
    label,
}: Props) {
    const { t, i18n } = useTranslation();
    const createPriceMutation = useCreateServiceSellPrice();
    const [draft, setDraft] = useState<ServiceSellPrice>(price);
    const [opened, setOpened] = useState(false);
    const [applyToLinkedService, setApplyToLinkedService] = useState(false);
    const [showForeignerPrice, setShowForeignerPrice] = useState(!!price.foreigner_amount);

    useEffect(() => {
        setDraft(price);
        setShowForeignerPrice(!!price.foreigner_amount);
    }, [price]);

    const handleOpen = () => {
        setDraft(price);
        setShowForeignerPrice(!!price.foreigner_amount);
        setApplyToLinkedService(false);
        setOpened(true);
    };

    const handleClose = () => {
        setOpened(false);
        setApplyToLinkedService(false);
        setDraft(price);
        setShowForeignerPrice(!!price.foreigner_amount);
    };

    const save = async () => {
        if (!service?.id) return;

        const shouldApplyToLinked = Boolean(service.linked_service_id && applyToLinkedService);

        createPriceMutation.mutate({
            ...draft,
            business_id: null,
            apply_to_linked_service: shouldApplyToLinked,
        }, {
            onSuccess: () => {
                notify.success(
                    shouldApplyToLinked ? t('saved_to_linked_service') : t('price_saved_successfully'),
                    ''
                );
                setOpened(false);
            }
        });
    };

    const dateRangeLabel = `${price.start_day ? formatDay(price.start_day) : '—'} – ${price.end_day ? formatDay(price.end_day) : '—'}`;

    return (
        <>
            <Tooltip label={`${t('edit_price')} (${dateRangeLabel})`} withArrow>
                <Button
                    color="gray"
                    size={label ? "compact-xs" : "xs"}
                    variant={label ? "default" : "subtle"}
                    leftSection={label ? <IconEdit size={11} stroke={1.8} /> : undefined}
                    onClick={handleOpen}
                    className={label ? "text-[10px] font-medium px-1.5 h-5" : undefined}
                >
                    {label || <IconEdit size={14} stroke={1.5} />}
                </Button>
            </Tooltip>

            <CardModal
                opened={opened}
                onClose={handleClose}
                size="fit"
                header={
                    <div className="flex items-center gap-2 font-semibold">
                        <IconEdit size={20} stroke={1.5} />
                        <span>{t('edit_price')}</span>
                    </div>
                }
                footer={
                    <div className="flex justify-between gap-2">
                        <Button
                            color="black"
                            variant="subtle"
                            size="xs"
                            onClick={handleClose}
                        >
                            {t('cancel')}
                        </Button>
                        <Button
                            color="black"
                            size="xs"
                            radius="xl"
                            loading={createPriceMutation.isPending}
                            onClick={save}
                            disabled={!draft?.start_day || !draft?.end_day || draft?.default_amount === null || draft?.default_amount === undefined}
                        >
                            {t('save')}
                        </Button>
                    </div>
                }
            >
                <div className="space-y-4">
                    {/* Date Inputs - Separated start_day and end_day */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <DatePickerInput
                            label={t('start_date')}
                            placeholder={t('select_date')}
                            leftSection={<IconCalendar size={16} stroke={1.5} />}
                            value={draft.start_day ? dayjs(draft.start_day).toDate() : null}
                            onChange={(date) => {
                                const formatted = date ? dayjs(date).format('YYYY-MM-DD') : '';
                                setDraft((prev) => ({
                                    ...prev,
                                    start_day: formatted,
                                    end_day: prev.end_day && formatted && dayjs(prev.end_day).isBefore(dayjs(formatted), 'day') ? '' : prev.end_day,
                                }));
                            }}
                            clearable
                            valueFormat="DD MMMM, YYYY"
                            locale={i18n.language}
                            popoverProps={{ withinPortal: true }}
                        />
                        <DatePickerInput
                            label={t('end_date')}
                            placeholder={t('select_date')}
                            leftSection={<IconCalendar size={16} stroke={1.5} />}
                            value={draft.end_day ? dayjs(draft.end_day).toDate() : null}
                            minDate={draft.start_day ? dayjs(draft.start_day).toDate() : undefined}
                            onChange={(date) => {
                                setDraft((prev) => ({
                                    ...prev,
                                    end_day: date ? dayjs(date).format('YYYY-MM-DD') : '',
                                }));
                            }}
                            clearable
                            valueFormat="DD MMMM, YYYY"
                            locale={i18n.language}
                            popoverProps={{ withinPortal: true }}
                        />
                    </div>

                    {/* Currency Selector */}
                    <CurrencySelector
                        value={draft?.default_currency || DEFAULT_CURRENCY}
                        onChange={currency => setDraft(prev => ({
                            ...prev,
                            default_currency: currency,
                            child_currency: currency,
                            senior_currency: currency,
                            infant_currency: currency,
                        }))}
                    />

                    {/* Standard Price Inputs */}
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                        <MoneyNumberInput
                            amount={draft?.default_amount}
                            onChange={amount => setDraft(prev => ({ ...prev, default_amount: amount }))}
                            label={t('price') + ' * ' + t('default')}
                        />
                        <MoneyNumberInput
                            amount={draft?.child_amount}
                            onChange={amount => setDraft(prev => ({ ...prev, child_amount: amount }))}
                            label={t('children')}
                        />
                        <MoneyNumberInput
                            amount={draft?.senior_amount}
                            onChange={amount => setDraft(prev => ({ ...prev, senior_amount: amount }))}
                            label={service.show_retired ? t('retired_plural') : t('seniors')}
                        />
                        <MoneyNumberInput
                            amount={draft?.infant_amount}
                            onChange={amount => setDraft(prev => ({ ...prev, infant_amount: amount }))}
                            label={t('infants')}
                        />
                    </div>

                    {/* Foreigner Price */}
                    {showForeignerPrice ? (
                        <div className="solid-box-border-blue space-y-2">
                            <div className="flex items-center justify-between">
                                <span className="font-medium text-sm">{t('foreigner_price')}</span>
                                <Button
                                    variant="subtle"
                                    color="red"
                                    size="compact-xs"
                                    onClick={() => {
                                        setShowForeignerPrice(false);
                                        setDraft(prev => ({
                                            ...prev,
                                            foreigner_amount: null,
                                        }));
                                    }}
                                >
                                    {t('remove')}
                                </Button>
                            </div>
                            <CurrencySelector
                                value={draft?.foreigner_currency || DEFAULT_CURRENCY}
                                onChange={currency => setDraft(prev => ({
                                    ...prev,
                                    foreigner_currency: currency,
                                }))}
                            />
                            <MoneyNumberInput
                                className="w-full"
                                amount={draft?.foreigner_amount}
                                onChange={amount => setDraft(prev => ({ ...prev, foreigner_amount: amount }))}
                                label={t('price') + ' * ' + t('foreigner')}
                            />
                        </div>
                    ) : (
                        <div>
                            <Button
                                variant="subtle"
                                size="xs"
                                radius="xl"
                                leftSection={<IconPlus size={14} />}
                                onClick={() => setShowForeignerPrice(true)}
                            >
                                {t('enter_different_foreigner_price')}
                            </Button>
                        </div>
                    )}

                    {Boolean(service.linked_service_id) && (
                        <div className="solid-box-border">
                            <Switch
                                color="black"
                                checked={applyToLinkedService}
                                onChange={(e) => setApplyToLinkedService(e.currentTarget.checked)}
                                label={t('save_to_linked_service')}
                                description={t('do_you_want_to_apply_this_price_to_linked_service')}
                            />
                        </div>
                    )}
                </div>
            </CardModal>
        </>
    );
}
